import "./coeditor.less";

import {css, StyleSheet} from "aphrodite";
import {useCallback, useContext, useState} from "react";
import {UNSAFE_DataRouterContext} from "react-router-dom-v5-compat";
import {Editable, Slate} from "slate-react";

import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    coeditingRequestEditCommandName,
    coeditingRequestInsertCommandName,
    getCoeditorVariables,
    useCoeditorContext,
} from "@khan/guide-coeditor-core";
import {
    useGuideAudioController,
    useSendChatMessage,
    useSendGuideCommand,
} from "@khan/guide-core";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {useLocation} from "@khan/urls";

import AskKhanmigoPopover from "./ask-khanmigo-popover.tsx";
import {CheckForUnsavedChangesRRv5} from "./check-for-unsaved-changes-rrv5.tsx";
import {CheckForUnsavedChangesRRv6} from "./check-for-unsaved-changes-rrv6.tsx";
import {toolbarHeight} from "./constants.ts";
import useCoeditorAutosave from "./hooks/use-coeditor-autosave.ts";
import {handleKeyDown} from "./slate-key-bindings.ts";
import {renderElement, renderLeaf} from "./slate-renderers.tsx";
import {getCursorDecorationForNode} from "./slate/helpers/decoration-helpers.ts";
import {
    rerenderTextNodes,
    unsetDomSelectionFromEditor,
} from "./slate/helpers/editor-helpers.ts";
import {
    hasTextChangedSinceInitialEdit,
    saveCurrentSelection,
} from "./slate/helpers/insertion-helpers.ts";
import type {Suggestion} from "./suggestions.ts";
import Toolbar from "./toolbar.tsx";
import type {AutosavingStatus} from "./types.ts";

export default function Coeditor(props: {
    editorId?: string;
    onChatSuggestionClicked?: () => void;
    showDocumentActionButtons?: boolean;
    viewOnly?: boolean;
    setSavingStatus?: (savingStatus: AutosavingStatus) => void;
}) {
    const {
        editorId,
        onChatSuggestionClicked,
        showDocumentActionButtons,
        viewOnly = false,
        setSavingStatus,
    } = props;

    const [hasPublishedEditedEvent, setHasPublishedEditedEvent] =
        useState(false);
    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const sendGuideCommand = useSendGuideCommand();
    const sendChatMessage = useSendChatMessage();
    const {href} = useLocation();
    const {editor, documentTitle, readOnly} = useCoeditorContext();
    const {isVoiceOn, selectedVoice} = useGuideAudioController();

    const isRRv6 = !!useContext(UNSAFE_DataRouterContext);

    useCoeditorAutosave(editor, documentTitle, setSavingStatus);

    const onChange = useCallback(() => {
        if (
            threadId &&
            editor &&
            !hasPublishedEditedEvent &&
            hasTextChangedSinceInitialEdit(editor)
        ) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorDocumentEdited",
                eventSchemaVersion: 2,
                aiGuideThreadId: threadId,
                aiGuideNavigationId: navigation.id,
                currentPageURL: href,
            });

            setHasPublishedEditedEvent(true);
        }
    }, [threadId, hasPublishedEditedEvent, href, navigation.id, editor]);

    const onKeyDown = useCallback(
        (evt) => editor && handleKeyDown(editor, evt),
        [editor],
    );

    const handleSuggestionClick = useCallback(
        ({kind, text, additionalText}: Suggestion) => {
            const message = additionalText
                ? `${text()}: ${additionalText}`
                : text();

            if (kind === "change") {
                // Change commands should behave like user-sent messages:
                // When the command finishes, the conversation will be updated
                // with the user-sent request and Khanmigo's response, but we
                // need to temporarily update the conversation here to show
                // the user's requested change as an outgoing message. This is
                // already handled in the chat path by `sendChatMessage`, but
                // we need to do it ourselves here for `sendGuideCommand`.
                GuideStateApi.set("conversation", (conversation) => [
                    ...conversation,
                    {
                        type: "outgoing" as const,
                        text: message,
                        user: "Student" as const,
                        uiOnly: true,
                        serverOnly: false,
                        interactionId: "",
                    },
                ]);

                const {prefix, selection, suffix} = getCoeditorVariables();

                if (!selection) {
                    return sendGuideCommand(coeditingRequestInsertCommandName, {
                        prefix: prefix ?? "",
                        selection: selection ?? "",
                        suffix: suffix ?? "",
                        instruction: message,
                    });
                }

                return sendGuideCommand(coeditingRequestEditCommandName, {
                    prefix: prefix ?? "",
                    selection: selection ?? "",
                    suffix: suffix ?? "",
                    instruction: message,
                });
            }

            onChatSuggestionClicked?.();

            return sendChatMessage(message, {
                containsSpeechToTextContent: false,
                guideVoiceDescriptor: isVoiceOn ? selectedVoice : null,
                requestUIType: "USER_TYPED",
                prepopulatedText: message,
                requestWithTextToSpeech: isVoiceOn,
                // If the suggestion isn't an "edit" kind, a user would
                // typically need to provide their own message to be sent
                // in the chat. We want to track the time it takes for
                // the user to submit their own message, so that we have
                // a more accurate measure of the time spent actively
                // engaging with the AI Guide.
                // TODO: compute the time it takes for the user to submit
                timeToSubmit: 0,
            });
        },
        [
            sendGuideCommand,
            sendChatMessage,
            isVoiceOn,
            selectedVoice,
            onChatSuggestionClicked,
        ],
    );

    const onBlur = useCallback(() => {
        saveCurrentSelection(editor);

        // By default, slate-react has some tricky logic that runs on blur to
        // attempt to update the editor's selection based on the new DOM
        // selection. But we don't want this; we want to preserve the editor
        // selection when focus leaves (so that, for example, a user can move
        // focus into the suggestions popover without losing what they have
        // highlighted). This little trick lets us do that:
        unsetDomSelectionFromEditor(editor);

        // A bit of a hack: we want to force the editor to draw decorations on
        // blur, so we update a useless property on selected text nodes here.
        rerenderTextNodes(editor);
    }, [editor]);

    const decorate = useCallback(
        ([node, path]) => {
            const decoration = getCursorDecorationForNode(editor, node, path);
            return decoration ? [decoration] : [];
        },
        [editor],
    );

    if (!editor) {
        return null;
    }

    return (
        <>
            {isRRv6 ? (
                <CheckForUnsavedChangesRRv6
                    editor={editor}
                    viewOnly={viewOnly}
                />
            ) : (
                <CheckForUnsavedChangesRRv5
                    editor={editor}
                    viewOnly={viewOnly}
                />
            )}
            <Slate
                editor={editor}
                initialValue={editor.children}
                onChange={onChange}
            >
                {!viewOnly && (
                    <Toolbar
                        readOnly={readOnly}
                        showDocumentActionButtons={showDocumentActionButtons}
                    />
                )}
                {readOnly && <View style={styles.scrim} />}
                <Editable
                    data-testid="ai-guide-coeditor"
                    as="article"
                    id={editorId}
                    className={css(
                        styles.editable,
                        readOnly && styles.readOnly,
                    )}
                    readOnly={readOnly || viewOnly}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    decorate={decorate}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    spellCheck={false}
                    // prevents browser focus ring which
                    //   blocks cursor from being visible
                    style={{outline: "none"}}
                />
                {!viewOnly && (
                    <AskKhanmigoPopover
                        handleSuggestionClick={handleSuggestionClick}
                    />
                )}
            </Slate>
        </>
    );
}

const styles = StyleSheet.create({
    editable: {
        height: "100%",
        padding: `${spacing.large_24}px ${spacing.xxLarge_48}px`,
        overflow: "auto",
    },
    readOnly: {
        pointerEvents: "none",
    },
    scrim: {
        position: "absolute",
        width: "100%",
        height: `calc(100% - ${toolbarHeight}px)`,
        top: toolbarHeight,
        backgroundColor: "grey",
        opacity: 0.05,
    },
});
